import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParameterDeclaration, parametersOf, parse } from '@the-real-insight/tri-model';
import { Building2, Grid2x2, LandPlot, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import FormulaEditor from './FormulaEditor';
import { Score, deleteScore, newScoreId, saveScore, variablesFromScoringCatalog } from './scores';
import { Distribution, Granularity, ScoringMetric } from './MetricsAPI';
import { option, optionBullet, optionLabel, optionList, optionSelected, sectionHint } from './optionStyles';

/**
 * The scores: a list of user-written formulas, and the dialog that writes them.
 *
 * A score is drawn like a metric — pick one and the map fills — so the list behaves like the metric
 * list beside it. The two are mutually exclusive: the map shows one thing at a time, and a score
 * *is* the thing rather than a layer on top of a metric.
 */

interface Properties {
    scores: Score[];
    onScoresChange: (scores: Score[]) => void;
    selectedScoreId: string | null;
    onSelect: (id: string | null) => void;
    /** Every metric a formula may use; the editor shows how each reaches the chosen granularity. */
    catalog: ScoringMetric[];
    /** Current slider values of the selected score, by parameter name. */
    parameterValues: Record<string, number>;
    onParameterChange: (name: string, value: number) => void;
    /** Liegenschaften des gewählten Projekts mit Lage; 0 blendet den Schalter aus. */
    projectLocationCount: number;
    onlyProject: boolean;
    onOnlyProjectChange: (on: boolean) => void;
}

const emptyScore = (): Score => ({ id: newScoreId(), name: '', description: '', formula: '', granularity: 'cell', distribution: 'buildings' });

/** Kacheln zuerst: sie gibt es überall, Flurstücke und Gebäude nur, wo sie eingelesen sind. */
const GRANULARITIES: { key: Granularity; icon: typeof Grid2x2 }[] = [
    { key: 'cell', icon: Grid2x2 },
    { key: 'parcel', icon: LandPlot },
    { key: 'building', icon: Building2 },
];
const DISTRIBUTIONS: Distribution[] = ['buildings', 'area'];

export default function ScoreSection({
    scores,
    onScoresChange,
    selectedScoreId,
    onSelect,
    catalog,
    parameterValues,
    onParameterChange,
    projectLocationCount,
    onlyProject,
    onOnlyProjectChange,
}: Properties) {
    const { t, i18n } = useTranslation();
    const nameFieldId = useId();
    const descriptionFieldId = useId();
    const [editing, setEditing] = useState<Score | null>(null);
    const variables = useMemo(
        () => variablesFromScoringCatalog(catalog, t, i18n.language, editing?.granularity, editing?.distribution),
        [catalog, t, i18n.language, editing?.granularity, editing?.distribution],
    );

    const formulaError = editing ? parse(editing.formula).error : null;

    // A score's knobs belong to the score, so they are read from its formula rather than stored
    // beside it — editing the formula cannot leave a stale slider behind.
    const selected = scores.find((score) => score.id === selectedScoreId) ?? null;
    const parameters: ParameterDeclaration[] = selected ? parametersOf(selected.formula) : [];
    const complete = !!editing && editing.name.trim().length > 0 && editing.formula.trim().length > 0 && !formulaError;

    const [saving, setSaving] = useState(false);
    const commit = async () => {
        if (!editing || !complete) return;
        setSaving(true);
        try {
            onScoresChange(await saveScore(scores, { ...editing, name: editing.name.trim() }));
            onSelect(editing.id);
            setEditing(null);
        } catch (error: any) {
            toast.error(error?.message ?? t('global.generalError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className={optionList}>
                {scores.map((score) => {
                    const isSelected = score.id === selectedScoreId;
                    return (
                        <div key={score.id} className="flex items-center gap-0.5">
                            <button
                                type="button"
                                aria-pressed={isSelected}
                                className={cn(option, isSelected && optionSelected)}
                                onClick={() => onSelect(isSelected ? null : score.id)}
                            >
                                <span className={optionBullet} />
                                <span className={optionLabel} title={score.description || score.formula}>
                                    {score.name}
                                </span>
                                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                                    {t(`heatmap.scores.granularityShort.${score.granularity ?? 'cell'}`)}
                                </span>
                            </button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="shrink-0 text-muted-foreground"
                                title={t('global.edit')}
                                aria-label={t('global.edit')}
                                onClick={() => setEditing(score)}
                            >
                                <Pencil size={13} strokeWidth={1.8} />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="shrink-0 text-muted-foreground"
                                title={t('global.delete')}
                                aria-label={t('global.delete')}
                                onClick={() => {
                                    deleteScore(scores, score.id)
                                        .then((next) => {
                                            onScoresChange(next);
                                            if (isSelected) onSelect(null);
                                        })
                                        .catch((error: any) => toast.error(error?.message ?? t('global.generalError')));
                                }}
                            >
                                <Trash2 size={13} strokeWidth={1.8} />
                            </Button>
                        </div>
                    );
                })}
            </div>

            {projectLocationCount > 0 ? (
                <label className="mt-2 flex items-start gap-2 border-t pt-2 text-sm" data-testid="score-only-project">
                    <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={onlyProject}
                        onChange={(event) => onOnlyProjectChange(event.target.checked)}
                    />
                    <span className="flex flex-col gap-0.5">
                        <span>{t('heatmap.scores.onlyProject', { count: projectLocationCount })}</span>
                        <span className="text-[11px] text-muted-foreground">{t('heatmap.scores.onlyProjectHint')}</span>
                    </span>
                </label>
            ) : (
                <></>
            )}

            {parameters.length > 0 ? (
                <div className="mt-2 flex flex-col gap-2 border-t pt-2">
                    {parameters.map((parameter) => (
                        <div key={parameter.name} className="flex flex-col gap-1.5">
                            {/* A span, not a `Label`: the element a `for` would point at is the
                                slider's root, and only the thumb inside it carries the slider role. */}
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-sm font-medium leading-none">{parameter.name}</span>
                                <span className="text-xs text-muted-foreground">
                                    {parameterValues[parameter.name] ?? parameter.defaultValue}
                                </span>
                            </div>
                            <Slider
                                min={parameter.min}
                                max={parameter.max}
                                // A hundred steps across the range: fine enough to tune, coarse enough
                                // that dragging does not fire a query per pixel.
                                step={(parameter.max - parameter.min) / 100 || 1}
                                value={[parameterValues[parameter.name] ?? parameter.defaultValue]}
                                onValueChange={(next) => {
                                    const value = next[0];
                                    if (value !== undefined) onParameterChange(parameter.name, value);
                                }}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <></>
            )}

            <Button
                type="button"
                variant="outline"
                className="mt-1.5 w-full justify-start gap-1.5 border-dashed"
                onClick={() => setEditing(emptyScore())}
            >
                <Plus size={14} strokeWidth={2} />
                {t('heatmap.scores.add')}
            </Button>

            {scores.length === 0 ? (
                <div className={cn(sectionHint, 'mt-3')}>{t('heatmap.scores.none')}</div>
            ) : (
                <></>
            )}

            <Dialog
                open={!!editing}
                onOpenChange={(next) => {
                    if (!next) setEditing(null);
                }}
            >
                <DialogContent
                    className="w-[calc(100vw-2rem)] max-w-none sm:max-w-[720px]"
                    // Fluent's `isBlocking`: a click beside the dialog must not throw away what
                    // has been typed into it.
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('heatmap.scores.dialogTitle')}</DialogTitle>
                        <DialogDescription>{t('heatmap.scores.dialogSubText')}</DialogDescription>
                    </DialogHeader>

                    {editing ? (
                        <div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor={nameFieldId}>{t('heatmap.scores.name')}</Label>
                                <Input
                                    id={nameFieldId}
                                    value={editing.name}
                                    onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                                />
                            </div>
                            <div className="mt-3 flex flex-col gap-1.5">
                                <Label htmlFor={descriptionFieldId}>{t('heatmap.scores.description')}</Label>
                                <Textarea
                                    id={descriptionFieldId}
                                    value={editing.description}
                                    rows={2}
                                    className="min-h-0 resize-none"
                                    onChange={(event) =>
                                        setEditing({ ...editing, description: event.target.value })
                                    }
                                />
                            </div>
                            <div className="mt-5 flex flex-col gap-1.5">
                                <div className={sectionHint}>{t('heatmap.scores.granularity')}</div>
                                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('heatmap.scores.granularity')}>
                                    {GRANULARITIES.map(({ key, icon: Icon }) => (
                                        <Button
                                            key={key}
                                            type="button"
                                            size="sm"
                                            role="radio"
                                            aria-checked={editing.granularity === key}
                                            variant={editing.granularity === key ? 'default' : 'outline'}
                                            className="gap-1.5"
                                            onClick={() => setEditing({ ...editing, granularity: key })}
                                            data-granularity={key}
                                        >
                                            <Icon size={14} strokeWidth={1.8} aria-hidden />
                                            {t(`heatmap.scores.granularityOption.${key}`)}
                                        </Button>
                                    ))}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {t(`heatmap.scores.granularityHint.${editing.granularity}`)}
                                </div>
                                {editing.granularity === 'parcel' ? (
                                    <label className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                        <span className="text-muted-foreground">{t('heatmap.scores.distribution')}</span>
                                        <select
                                            className="h-7 rounded-md border bg-card px-2 text-xs"
                                            value={editing.distribution}
                                            onChange={(event) =>
                                                setEditing({ ...editing, distribution: event.target.value as Distribution })
                                            }
                                            data-testid="score-distribution"
                                        >
                                            {DISTRIBUTIONS.map((key) => (
                                                <option key={key} value={key}>
                                                    {t(`heatmap.scores.distributionOption.${key}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : (
                                    <></>
                                )}
                            </div>
                            <div className="mt-5">
                                <div className={sectionHint}>{t('heatmap.scores.formula')}</div>
                                <FormulaEditor
                                    value={editing.formula}
                                    onChange={(formula) => setEditing({ ...editing, formula })}
                                    variables={variables}
                                />
                                <div className="mt-1 text-[11px] text-muted-foreground">{t('heatmap.scores.functionsHint')}</div>
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                            {t('global.cancel')}
                        </Button>
                        <Button type="button" disabled={!complete || saving} onClick={() => void commit()}>
                            {t('global.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
